import { useRef, useCallback, useEffect } from 'react';

export interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

export interface UseScrollAwareTapOptions {
  onTap?: () => void;
  scrollThreshold?: number;
  timeThreshold?: number;
}

export interface UseScrollAwareTapReturn {
  touchHandlers: {
    onTouchStart: (event: React.TouchEvent) => void;
    onTouchMove: (event: React.TouchEvent) => void;
    onTouchEnd: (event: React.TouchEvent) => void;
  };
}

/**
 * useScrollAwareTap - Custom hook that distinguishes between scroll gestures and intentional taps
 * 
 * This prevents show details from opening when users are scrolling and their finger
 * happens to be on a show tile. Only fires onTap for genuine tap gestures.
 * 
 * Features:
 * - Detects scroll vs tap based on movement distance and time
 * - Prevents accidental taps during scrolling
 * - Works with both vertical page scrolling and horizontal carousel scrolling
 * - Optimized for mobile touch interactions
 */
export const useScrollAwareTap = (options: UseScrollAwareTapOptions = {}): UseScrollAwareTapReturn => {
  const {
    onTap,
    scrollThreshold = 10, // pixels - if finger moves more than this, it's a scroll
    timeThreshold = 500    // ms - if touch lasts longer than this, it's probably a scroll
  } = options;

  const touchStartRef = useRef<TouchPoint | null>(null);
  const hasMoved = useRef<boolean>(false);
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle touch start - record initial position
  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;

    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };

    hasMoved.current = false;
  }, []);

  // Handle touch move - detect if this is a scroll gesture
  const handleTouchMove = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    const touchStart = touchStartRef.current;
    
    if (!touch || !touchStart) return;

    // Calculate movement distance
    const deltaX = Math.abs(touch.clientX - touchStart.x);
    const deltaY = Math.abs(touch.clientY - touchStart.y);
    const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // If finger has moved beyond threshold, this is a scroll gesture
    if (totalMovement > scrollThreshold) {
      hasMoved.current = true;
      // Prevent default to avoid any click events from firing
      event.preventDefault();
    }
  }, [scrollThreshold]);

  // Handle touch end - only fire tap if it wasn't a scroll
  const handleTouchEnd = useCallback((event: React.TouchEvent) => {
    const touchStart = touchStartRef.current;
    
    if (!touchStart) return;

    const touchDuration = Date.now() - touchStart.time;
    
    // If this was detected as a scroll gesture, prevent any tap
    if (hasMoved.current) {
      event.preventDefault();
      touchStartRef.current = null;
      hasMoved.current = false;
      return;
    }
    
    // Only fire tap if:
    // 1. Finger didn't move much (not a scroll)
    // 2. Touch was quick (not a long press)
    // 3. We have a tap handler
    if (touchDuration < timeThreshold && onTap) {
      // Prevent default to avoid any click events
      event.preventDefault();
      // Small delay to ensure any scroll momentum has started
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }

      tapTimeoutRef.current = setTimeout(() => {
        if (!hasMoved.current) {
          onTap();
        }
        if (tapTimeoutRef.current) {
          clearTimeout(tapTimeoutRef.current);
          tapTimeoutRef.current = null;
        }
      }, 100); // Increased delay for better scroll detection
    }

    // Reset state
    touchStartRef.current = null;
    hasMoved.current = false;
  }, [onTap, timeThreshold]);

  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
        tapTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd
    }
  };
};
